import React, { useEffect, useRef, useState, useCallback } from 'react';
import * as atlas from 'azure-maps-control';
import { useAppSettings } from '~/AppSettingsContext';
import { MapPin, Users, Stethoscope, Layers } from 'lucide-react';
import 'azure-maps-control/dist/atlas.min.css';
import { getZipCodeTotalsOptions, type ZipCodeTotalsViewModel } from '@generated';
import { useSuspenseQuery } from '@tanstack/react-query';

interface LayerConfig {
	key: keyof ZipCodeTotalsViewModel;
	label: string;
	icon: React.ReactNode;
	heatColor: string[];
	markerColor: string;
}

const LAYER_CONFIGS: LayerConfig[] = [
	{
		key: 'referrals',
		label: 'New Referrals',
		icon: <MapPin className="size-4" />,
		heatColor: [
			'interpolate',
			['linear'],
			['heatmap-density'],
			0,
			'transparent',
			0.2,
			'#fef08a',
			0.4,
			'#fbbf24',
			0.6,
			'#f59e0b',
			0.8,
			'#ea580c',
			1,
			'#dc2626'
		] as unknown as string[],
		markerColor: '#dc2626'
	},
	{
		key: 'onServicePatients',
		label: 'Existing Patients',
		icon: <Users className="size-4" />,
		heatColor: [
			'interpolate',
			['linear'],
			['heatmap-density'],
			0,
			'transparent',
			0.2,
			'#bfdbfe',
			0.4,
			'#60a5fa',
			0.6,
			'#3b82f6',
			0.8,
			'#2563eb',
			1,
			'#1d4ed8'
		] as unknown as string[],
		markerColor: '#2563eb'
	},
	{
		key: 'clinicians',
		label: 'Clinician Locations',
		icon: <Stethoscope className="size-4" />,
		heatColor: [
			'interpolate',
			['linear'],
			['heatmap-density'],
			0,
			'transparent',
			0.2,
			'#bbf7d0',
			0.4,
			'#4ade80',
			0.6,
			'#22c55e',
			0.8,
			'#16a34a',
			1,
			'#15803d'
		] as unknown as string[],
		markerColor: '#16a34a'
	}
];

export interface ReferralHeatMapProps {
	className?: string;
}

export function ReferralHeatMap({ className }: ReferralHeatMapProps) {
	const { data } = useSuspenseQuery(getZipCodeTotalsOptions());
	const { atlasSubscriptionKey } = useAppSettings();
	const mapContainerRef = useRef<HTMLDivElement>(null);
	const mapRef = useRef<atlas.Map | null>(null);
	const [mapReady, setMapReady] = useState(false);
	const [activeLayers, setActiveLayers] = useState<Record<string, boolean>>({
		referrals: true,
		onServicePatients: true,
		clinicians: true
	});

	const toggleLayer = useCallback((key: string) => {
		setActiveLayers(prev => ({ ...prev, [key]: !prev[key] }));
	}, []);

	useEffect(() => {
		if (!mapContainerRef.current || mapRef.current) return;

		const map = new atlas.Map(mapContainerRef.current, {
			authOptions: {
				authType: atlas.AuthenticationType.subscriptionKey,
				subscriptionKey: atlasSubscriptionKey
			},
			center: [-80.0951841, 40.3787974],
			zoom: 10,
			style: 'grayscale_light'
		});

		map.events.add('ready', () => {
			setMapReady(true);
		});

		mapRef.current = map;

		return () => {
			map.dispose();
			mapRef.current = null;
			setMapReady(false);
		};
	}, [atlasSubscriptionKey]);

	// Manage layers based on toggle state
	useEffect(() => {
		if (!mapReady || !mapRef.current) return;

		const map = mapRef.current;

		for (const config of LAYER_CONFIGS) {
			const sourceId = `heatmap-source-${config.key}`;
			const heatLayerId = `heatmap-layer-${config.key}`;
			const symbolLayerId = `symbol-layer-${config.key}`;
			const isActive = activeLayers[config.key];
			const points = data[config.key];

			// Remove existing layers and source for this data type
			try {
				map.layers.remove(heatLayerId);
			} catch {
				/* doesn't exist yet */
			}
			try {
				map.layers.remove(symbolLayerId);
			} catch {
				/* doesn't exist yet */
			}
			try {
				map.sources.remove(sourceId);
			} catch {
				/* doesn't exist yet */
			}

			if (!isActive || points.length === 0) continue;

			// Create data source
			const source = new atlas.source.DataSource(sourceId);
			map.sources.add(source);

			for (const point of points) {
				source.add(
					new atlas.data.Feature(new atlas.data.Point([point.longitude, point.latitude]), {
						weight: point.weight ?? 1,
						label: point.label ?? '',
						zipCode: point.zipCode ?? ''
					})
				);
			}

			// Add heat map layer
			map.layers.add(
				new atlas.layer.HeatMapLayer(source, heatLayerId, {
					weight: ['get', 'weight'],
					radius: 30,
					opacity: 0.7,
					color: config.heatColor as unknown as atlas.Expression
				})
			);

			// Add symbol layer for individual points (visible at higher zoom)
			map.layers.add(
				new atlas.layer.SymbolLayer(source, symbolLayerId, {
					iconOptions: {
						image: 'marker-flat',
						allowOverlap: true,
						size: 0.6
					},
					textOptions: {
						textField: ['get', 'zipCode'],
						offset: [0, 1.2],
						color: config.markerColor,
						size: 10,
						font: ['StandardFont-Bold']
					},
					minZoom: 12
				})
			);
		}
	}, [mapReady, activeLayers, data]);

	return (
		<div className={`flex flex-col gap-3 ${className ?? ''}`}>
			{/* Layer toggles */}
			<div className="flex flex-wrap items-center gap-2">
				<div className="flex items-center gap-1.5 text-sm font-medium text-gray-600">
					<Layers className="size-4" />
					<span>Layers:</span>
				</div>
				{LAYER_CONFIGS.map(config => {
					const isActive = activeLayers[config.key];
					return (
						<button
							key={config.key}
							type="button"
							onClick={() => toggleLayer(config.key)}
							className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
								isActive ? 'border-transparent text-white' : 'border-gray-300 bg-white text-gray-500 hover:bg-gray-50'
							}`}
							style={isActive ? { backgroundColor: config.markerColor } : undefined}
						>
							{config.icon}
							{config.label}
							<span className="ml-1 tabular-nums">({data[config.key].length})</span>
						</button>
					);
				})}
			</div>

			{/* Map */}
			<div className="relative overflow-hidden rounded-lg border border-gray-200">
				<div ref={mapContainerRef} className="h-[600px] w-full" />
				{!mapReady && (
					<div className="absolute inset-0 flex items-center justify-center bg-gray-50">
						<span className="text-sm text-gray-500">Loading map...</span>
					</div>
				)}
			</div>

			{/* Legend */}
			<div className="flex flex-wrap gap-4 text-xs text-gray-500">
				{LAYER_CONFIGS.filter(c => activeLayers[c.key]).map(config => (
					<div key={config.key} className="flex items-center gap-1.5">
						<span className="inline-block size-3 rounded-full" style={{ backgroundColor: config.markerColor }} />
						<span>{config.label}: higher density = more staffing needed</span>
					</div>
				))}
			</div>
		</div>
	);
}
